import React from 'react';
import { Feature, InjectorService, Toolbar, ToolbarService } from '@tencent/workbench';
import BaseToolbarFeature from '@framework/workbench/common/feature/base-toolbar-feature';
import { edgeColConfig, edgeRowConfig, firstConfig, lastConfig, quickConfig, titleConfig, totalConfig } from './config';
import i18n from '@i18next/i18n';
import { ITableModel, ITblProps } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import DEFAULT_TABLE_STYLE from '@tencent/slide-sdk-model/src/template/default-table-style-template.json';
import SelectablePanel from '@framework/workbench/common/components/selectable-panel';
import TableCard from '@framework/workbench/common/components/selectable-panel/table-card';
import { DataType, ICTTableStyleDmlA } from '@tencent/slide-sdk-model';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { CheckboxPanel } from '@framework/workbench/common/components/checkbox-panel';
import { transaction } from '@src/slide/domain/slide-app';
import { IPlaceholderShapeData } from '@tencent/slide-sdk-model/src/data';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IPageDataModel } from '@tencent/slide-sdk-model/src/model/page-data-model';
import { ITableData } from '@tencent/slide-sdk-model/src/data/shape-data';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import {
    AlignType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';
import styles from './index.less';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';

const FIRST_STYLE_START_INDEX = 0;
const FIRST_STYLE_END_INDEX = 7;
const SECOND_STYLE_START_INDEX = 14;
const SECOND_STYLE_END_INDEX = 35;
const THIRD_STYLE_START_INDEX = 63;
const LAST_START_INDEX = 60;
const STYLE_ROW_NUMBER = 7;

@Feature()
export class TableTabFeature extends BaseToolbarFeature {
    public id = quickConfig.id;
    public toolbarService = InjectorService.get(ToolbarService);
    private cachedTableStyle = new Map<string, ITableModel>();
    public constructor() {
        super();
    }

    public getBorderStyleSelectionToolbarContent = () => <div>123123</div>;

    public getBorderWidthSelectionToolbar = () => <div>123123</div>;

    public onCellOperationClick = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        let action = TDWActionType.TABLE_TAB_STYLE_MERGE;
        if (tableModel?.selectOneMergedCell?.()) {
            action = TDWActionType.TABLE_TAB_STYLE_SPLIT;
        }
        this.getToolbarMenu()?.callback(MenuType.TOOLBAR, ActionType.MERGE_CELL);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action,
        });
    };

    public setCenterForMerge = () => {
        transaction(() => {
            this.getToolbarMenu()?.callback(
                MenuType.TOOLBAR,
                ActionType.HORIZONTAL_ALIGN,
                AlignType.HORIZONTAL_CENTER,
            );
        });
        transaction(() => {
            this.getToolbarMenu()?.callback(
                MenuType.TOOLBAR,
                ActionType.VERTICAL_ALIGN,
                AlignType.VERTICAL_CENTER,
            );
        });
    };

    public averageTableColWidth = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        tableModel?.averageTableColWidth();

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_DISTRIBUTE_COLUMNS,
        });
    };

    public averageTableRowHeight = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        tableModel?.averageTableRowHeight();

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_DISRTIBUTE_ROWS,
        });
    };


    public getCheckPanelContent = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        return <CheckboxPanel
            firstRowChecked={tableModel.firstRow}
            lastRowChecked={tableModel.lastRow}
            firstColChecked={tableModel.firstCol}
            lastColChecked={tableModel.lastCol}
            bandRowChecked={tableModel.bandRow}
            bandColChecked={tableModel.bandCol}
            onFirstRowClick={this.onFirstRowClick}
            onLastRowClick={this.onLastRowClick}
            onFirstColClick={this.onFirstColClick}
            onLastColClick={this.onLastColClick}
            onBandRowClick={this.onBandRowClick}
            onBandColClick={this.onBandColClick}
        />;
    };

    public clearStyleCache = () => {
        this.cachedTableStyle = new Map<string, ITableModel>();
    };

    public onFirstRowClick = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        this.updateTableProps({
            firstRow: !tableModel.firstRow,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_HEADER_ROW,
            ver5: !tableModel.firstRow ? 1 : 0,
        });
    };

    public onLastRowClick = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        this.updateTableProps({
            lastRow: !tableModel.lastRow,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_TOTAL_ROW,
            ver5: !tableModel.lastRow ? 1 : 0,
        });
    };

    public onFirstColClick = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        this.updateTableProps({
            firstCol: !tableModel.firstCol,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_FIRST_COLUMN,
            ver5: !tableModel.firstCol ? 1 : 0,
        });
    };

    public onLastColClick = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        this.updateTableProps({
            lastCol: !tableModel.lastCol,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_LAST_COLUMN,
            ver5: !tableModel.lastCol ? 1 : 0,
        });
    };

    public onBandRowClick = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        this.updateTableProps({
            bandRow: !tableModel.bandRow,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_BANDED_ROW,
            ver5: !tableModel.bandRow ? 1 : 0,
        });
    };

    public onBandColClick = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        this.updateTableProps({
            bandCol: !tableModel.bandCol,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_BANDED_COLUMN,
            ver5: !tableModel.bandCol ? 1 : 0,
        });
    };

    public getStyleList() {
        const tblStyleList = DEFAULT_TABLE_STYLE.tblStyleList.tblStyle;
        return tblStyleList.slice(1);
    }

    public updateTableProps(props: ITblProps) {
        const tableModel = this.getSelectModel() as ITableModel;
        if (tableModel.type !== UIModelType.TABLE) return;
        tableModel.updateTblProp(props);
        this.clearStyleCache();
    }

    public getCardComponent(tblStyleList: ICTTableStyleDmlA[]) {
        const tableModel = this.getSelectModel() as ITableModel;
        const slideModel = this.getSelectionService().currentSlide as ISlideModel;
        const { firstRow, firstCol, lastRow, lastCol, bandCol, bandRow } = tableModel;
        return [{
            name: i18n.t('最佳匹配'),
            // 8-14需要实现表格阴影
            // cardList: tblStyleList.slice(0, 14).map(item => ({
            cardList: tblStyleList.slice(FIRST_STYLE_START_INDEX, FIRST_STYLE_END_INDEX).map(item => ({
                element: <TableCard
                    tableStyleData={this.getTableData(item.styleId || '')}
                    slide={slideModel}
                    key={item.styleId}
                    id={item.styleId || ''}
                    name={item.styleName || ''}
                    tblStyle={item}
                    firstRow={firstRow}
                    firstCol={firstCol}
                    lastRow={lastRow}
                    lastCol={lastCol}
                    bandCol={bandCol}
                    bandRow={bandRow}
                    onClick={() => {
                        this.handleCardClick(item.styleId || '');
                    }}
                />,
                id: item.styleId || '',
            })),
        },
        {
            name: i18n.t('浅色'),
            cardList: tblStyleList.slice(SECOND_STYLE_START_INDEX, SECOND_STYLE_END_INDEX).map(item => ({
                element: <TableCard
                    tableStyleData={this.getTableData(item.styleId || '')}
                    slide={slideModel}
                    key={item.styleId}
                    id={item.styleId || ''}
                    name={item.styleName || ''}
                    tblStyle={item}
                    firstRow={firstRow}
                    firstCol={firstCol}
                    lastRow={lastRow}
                    lastCol={lastCol}
                    bandCol={bandCol}
                    bandRow={bandRow}
                    onClick={() => {
                        this.handleCardClick(item.styleId || '');
                    }}
                />,
                id: item.styleId || '',
            })),
        },
        {
            name: i18n.t('中等色'),
            cardList: tblStyleList.slice(SECOND_STYLE_END_INDEX, THIRD_STYLE_START_INDEX).map(item => ({
                element: <TableCard
                    tableStyleData={this.getTableData(item.styleId || '')}
                    slide={slideModel}
                    key={item.styleId}
                    id={item.styleId || ''}
                    name={item.styleName || ''}
                    tblStyle={item}
                    firstRow={firstRow}
                    firstCol={firstCol}
                    lastRow={lastRow}
                    lastCol={lastCol}
                    bandCol={bandCol}
                    bandRow={bandRow}
                    onClick={() => {
                        this.handleCardClick(item.styleId || '');
                    }}
                />,
                id: item.styleId || '',
            })),
        },
        {
            name: i18n.t('深色'),
            cardList: tblStyleList.slice(THIRD_STYLE_START_INDEX).map(item => ({
                element: <TableCard
                    tableStyleData={this.getTableData(item.styleId || '')}
                    slide={slideModel}
                    key={item.styleId}
                    id={item.styleId || ''}
                    name={item.styleName || ''}
                    tblStyle={item}
                    firstRow={firstRow}
                    firstCol={firstCol}
                    lastRow={lastRow}
                    lastCol={lastCol}
                    bandCol={bandCol}
                    bandRow={bandRow}
                    onClick={() => {
                        this.handleCardClick(item.styleId || '');
                    }}
                />,
                id: item.styleId || '',
            })),
        },
        ];
    }

    public onClearClick = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        transaction(() => {
            tableModel.clearAllCellFill();
            tableModel.updateTblProp({
                tableStyleId: '{58542034-FE4F-4ADA-92B8-4CA66D0F0DF3}',
            });
        });
    };

    public getContent() {
        const tableModel = this.getSelectModel() as ITableModel;
        const defaultShowIndex = this.getSelectedCardIndex(tableModel.tableStyleId);
        const tblStyleList = this.getStyleList();
        return <SelectablePanel
            cardStyle={{
                height: 38,
                borderRadius: 2,
            }}
            defaultShowIndex={defaultShowIndex}
            expandHeight={454}
            colNumber={7}
            showArrow={true}
            style={{
                width: 504,
            }}
            selectedId={tableModel.tableStyleId}
            appendElement={this.getAppendComponent()}
            childrenElements={
                this.getCardComponent(tblStyleList)
            } />;
    }

    public getAppendComponent = () => <div
        onMouseDown={(e) => {
            e.nativeEvent.stopImmediatePropagation();
            e.stopPropagation();
            e.preventDefault();
        }}
        onWheel={(e) => {
            e.nativeEvent.stopImmediatePropagation();
            e.stopPropagation();
        }}
        onClick={this.onClearClick}
        className={styles['clear-table-style-container']}>
        <div className={styles['clear-table-style-button']}>
            <div className={styles.icon}/>
            <div className={styles.text}>
                {
                    i18n.t('清除表格样式')
                }
            </div>
        </div>
    </div>;

    /**
     * 获取选择卡片的索引
     * @param tableStyleId
     * @private
     */
    private getSelectedCardIndex(tableStyleId: string | undefined) {
        if (!tableStyleId) return 0;
        const styleList = this.getStyleList();

        // 表格目前不支持效果，排除模版的效果行
        styleList.splice(7, 7);

        const cardIndex = styleList.findIndex(item => item.styleId === tableStyleId);

        if (cardIndex === -1) {
            return 0;
        }

        if (cardIndex >= LAST_START_INDEX) {
            return LAST_START_INDEX;
        }

        if (cardIndex < FIRST_STYLE_END_INDEX) {
            return 0;
        }

        const remainder = cardIndex % STYLE_ROW_NUMBER;
        return cardIndex - remainder;
    }

    private handleCardClick = (tableStyleId: string) => {
        const tableModel = this.getSelectModel() as ITableModel;
        if (tableModel.type !== UIModelType.TABLE) return;
        transaction(() => {
            tableModel.clearAllCellFill();
            tableModel.updateTblProp({
                tableStyleId,
            });
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_QUICK_STYLE,
            ver5: tableStyleId,
        });
    };

    private getCachedKey = (tableStyleId: string): string => `${tableStyleId}-${this.getThemeCachedString()}`;

    /**
     * 根据主题色获取缓存key，任何主题色的变更都会使缓存失效
     */
    private getThemeCachedString = () => {
        const tableModel = this.getSelectModel() as ITableModel;
        const { firstRow, firstCol, lastRow, lastCol, bandCol, bandRow } = tableModel;
        const currentThemeModel = this.getAppService().editableServiceLayer?.themeColorManager?.currentThemeModel;
        let cachedKey = '';
        if (currentThemeModel) {
            const clrSchemeKey = Object.keys(currentThemeModel.clrScheme);
            clrSchemeKey.forEach((key) => {
                cachedKey = cachedKey + currentThemeModel.clrScheme[key];
            });
        }

        return `${cachedKey}-${firstRow}-${firstCol}-${lastRow}-${lastCol}-${bandCol}-${bandRow}`;
    };

    private getTableData = (id: string) => {
        const tableData = this.getDefaultTableSdkData(id) as IPlaceholderShapeData;
        const slideModel = this.getSelectionService().currentSlide as ISlideModel;
        const cachedKey = this.getCachedKey(id);
        let appliedDestPhData;
        if (this.cachedTableStyle.has(cachedKey)) {
            appliedDestPhData = this.cachedTableStyle.get(cachedKey);
        } else {
            appliedDestPhData = SlideDataServices.SDKDataServices.DataHelper()?.getInheritedDataFromPlaceholder(
                tableData,
                slideModel.sdkData as IPageDataModel,
            ) as ITableData;
            this.cachedTableStyle.set(cachedKey, appliedDestPhData);
        }

        return appliedDestPhData;
    };

    private getDefaultTableSdkData(id: string) {
        const tableModel = this.getSelectModel() as ITableModel;
        const { firstRow, firstCol, lastRow, lastCol, bandCol, bandRow } = tableModel;
        const trList: any[] = [];
        for (let index = 0; index < 5; index++) {
            const tr = {
                tc: [{}, {}, {}, {}, {}],
            };
            trList.push(tr);
        }
        return {
            type: DataType.GraphicalObjectFrameTable,
            id: '',
            nvPr: {},
            spPr: {},
            graphic: {
                graphicData: {
                    tbl: {
                        tblPr: {
                            tableStyleId: id,
                            firstRow,
                            firstCol,
                            lastRow,
                            lastCol,
                            bandRow,
                            bandCol,
                        },
                        tr: trList,
                    },
                },
            },
        };
    }
}
